import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAlert } from '../components/AlertContext';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip, Legend as ReLegend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  AreaChart, Area
} from 'recharts';
import './Dashboard.css';
import './SupervisorDashboard.css';
import Sidebar from '../components/Sidebar';

const SupervisorDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useAlert();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState({});
  const [filters, setFilters] = useState({
    categoria: '',
    estado: '',
    periodo: 'all',
    startDate: '',
    endDate: '',
    departamento: 'Todas',
    estadoFuncionario: 'Activo',
    historialUsuario: '',
    clase: ''
  });

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const activeView = searchParams.get('tab') || 'inventory';
  const [funcData, setFuncData] = useState(null);

  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const apiBase = isLocal ? `http://${window.location.hostname}:3010` : `${window.location.protocol}//${window.location.hostname}`;

  const handleExportCSV = () => {
    if (!data?.equipos || data.equipos.length === 0) return toast.warning("No hay datos para exportar");

    const BOM = "\uFEFF";
    const headers = ["Codigo", "Modelo", "Estado", "Categoria", "Custodio", "Fecha Creado"];
    const rows = data.equipos.map(e => [
      `"${e.codigo || 'S/D'}"`,
      `"${e.modelo || 'S/D'}"`,
      `"${e.estado || 'S/D'}"`,
      `"${e.categoria || 'S/D'}"`,
      `"${e.custodio_nombre || 'Sin asignar'}"`,
      `"${e.creado_en ? e.creado_en.split(/[\sT]/)[0] : 'S/D'}"`
    ]);

    const csvContent = BOM + [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Reporte_Inventario_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.categoria) params.append('categoria', filters.categoria);
      if (filters.estado) params.append('estado', filters.estado);
      if (filters.historialUsuario) params.append('usuario', filters.historialUsuario);
      if (filters.clase) params.append('clase', filters.clase);

      let start, end;
      if (filters.periodo === 'all') {
        start = null;
        end = null;
      } else if (filters.periodo === 'custom') {
        start = filters.startDate;
        end = filters.endDate;
      } else {
        const endDateObj = new Date();
        const startDateObj = new Date();
        startDateObj.setDate(endDateObj.getDate() - parseInt(filters.periodo));
        start = startDateObj.toISOString().split('T')[0];
        end = endDateObj.toISOString().split('T')[0];
      }

      if (start) params.append('startDate', start);
      if (end) params.append('endDate', end);

      const resp = await fetch(`${apiBase}/api/stats/dashboard?${params.toString()}`, { credentials: 'include' });
      if (resp.ok) {
        const result = await resp.json();
        setData(result);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [apiBase, filters]);

  const fetchPersonnelData = useCallback(async () => {
    try {
      const { startDate, endDate, categoria, estado, departamento } = filters;
      const params = new URLSearchParams({
        startDate: startDate || '',
        endDate: endDate || '',
        categoria: categoria || '',
        estado: estado || '',
        departamento: departamento || ''
      });
      const resp = await fetch(`${apiBase}/api/stats/advanced-funcionarios?${params}`, { credentials: 'include' });
      if (resp.ok) {
        setFuncData(await resp.json());
      }
    } catch (err) {
      console.error(err);
    }
  }, [apiBase, filters]);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (!savedUser) return navigate('/');
    const u = JSON.parse(savedUser);
    const permisos = u.permisos || [];
    // Superadmin siempre entra, otros necesitan VER_ANALISIS
    if (u.rol !== 'superadmin' && !permisos.includes('VER_ANALISIS')) {
      return navigate('/dashboard');
    }
    setUser(u);
  }, [navigate]);

  useEffect(() => {
    if (user.rol) {
      fetchDashboardData();
    }
  }, [filters.periodo, filters.categoria, filters.estado, filters.startDate, filters.endDate, filters.historialUsuario, user.rol, fetchDashboardData]);

  useEffect(() => {
    if (activeView === 'personnel' && user.rol) {
      fetchPersonnelData();
    }
  }, [activeView, fetchPersonnelData, user.rol, filters.categoria, filters.estado]);

  const COLORS = [
    '#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#f43f5e', 
    '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#22c55e',
    '#3b82f6', '#64748b'
  ];

  const STATUS_COLORS = {
    'en servicio': '#10b981',
    'sin uso': '#94a3b8',
    'en mantenimiento': '#f59e0b',
    'en préstamo': '#6366f1',
    'extraviado': '#f43f5e',
    'sustraído': '#f43f5e',
    'dado de baja': '#64748b'
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    if (value === 0) return null;
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" style={{ fontSize: '10px', fontWeight: 'bold' }}>
        {value}
      </text>
    );
  };

  const renderExternalLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 20;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    if (value === 0) return null;
    return (
      <text x={x} y={y} fill="var(--p-navy)" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" style={{ fontSize: '11px', fontWeight: '800' }}>
        {`${name}: ${value}`}
      </text>
    );
  };

  const CATEGORIAS_POR_CLASE = {
    'Activo': ['PC', 'Notebook', 'Servidor', 'Tablet', 'Celular', 'Switch-Router'],
    'Periférico': ['Impresora', 'Monitor', 'Teclado', 'Mouse', 'Webcam', 'Escáner', 'UPS', 'Parlantes', 'Cargador']
  };

  const allCategories = useMemo(() => {
    return Array.from(new Set(Object.values(CATEGORIAS_POR_CLASE).flat())).sort();
  }, []);

  const statusPieData = useMemo(() => {
    if (!data?.statusDist) return [];
    return Object.entries(data.statusDist).map(([name, value]) => ({ name, value }));
  }, [data]);

  const categoryBarData = useMemo(() => {
    if (!data?.categoryDist) return [];
    return Object.entries(data.categoryDist)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [data]);

  const trendData = useMemo(() => data?.trends || [], [data]);


  if (isLoading) {
    return (
      <div className="dashboard-layout shimmer-bg">
        <Sidebar />
        <main className="main-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
          <div className="shimmer-loader-premium">
            <div className="shimmer-circle"></div>
            <div className="shimmer-text">
              <div className="shimmer-line long"></div>
              <div className="shimmer-line short"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const { history, alerts } = data || { history: [], alerts: [] };

  return (
    <div className="dashboard-layout">
      <Sidebar />

      <main className="main-content">
        <header className="content-header">
          <div className="header-title">
            <h1>Panel de Control de Activos</h1>
          </div>
          <div className="header-actions">


            <Link to="/perfil" className="user-profile">
              <span>{user.nombre || 'Mi Perfil'}</span>
              <div className="avatar">
                {user.nombre?.charAt(0).toUpperCase() || 'P'}
              </div>
            </Link>
          </div>
        </header>

        {/* Barra de Filtros Unificada Estilo Premium */}
        <section className="premium-filter-container animate-fade-in" style={{ marginBottom: '30px' }}>
          <div className="filter-shelf" style={{ 
            display: 'flex', 
            gap: '15px', 
            alignItems: 'center', 
            background: 'var(--table-head-bg)', 
            padding: '12px 20px', 
            borderRadius: '16px',
            border: '1px solid var(--glass-border)',
            backdropFilter: 'blur(10px)',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontSize: '10px', fontWeight: '800', color: 'var(--p-slate)', textTransform: 'uppercase' }}>Periodo:</label>
              <select className="select-premium" style={{ width: '160px', height: '38px' }} value={filters.periodo} onChange={e => setFilters({ ...filters, periodo: e.target.value })}>
                <option value="all">Histórico Total</option>
                <option value="7">Última Semana</option>
                <option value="30">Último Mes</option>
                <option value="90">Último Trimestre</option>
                <option value="custom">Rango Fecha</option>
              </select>
            </div>

            {filters.periodo === 'custom' && (
               <div style={{ display: 'flex', gap: '8px' }}>
                <input type="date" className="filter-select-premium" style={{ width: '130px', height: '38px', padding: '0 10px' }} value={filters.startDate} onChange={e => setFilters({ ...filters, startDate: e.target.value })} />
                <input type="date" className="filter-select-premium" style={{ width: '130px', height: '38px', padding: '0 10px' }} value={filters.endDate} onChange={e => setFilters({ ...filters, endDate: e.target.value })} />
              </div>
            )}

            <div style={{ width: '1px', height: '20px', background: 'var(--glass-border)' }}></div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontSize: '10px', fontWeight: '800', color: 'var(--p-slate)', textTransform: 'uppercase' }}>Clase:</label>
              <select 
                className="select-premium" 
                style={{ width: '140px', height: '38px' }} 
                value={filters.clase} 
                onChange={e => {
                  const newClase = e.target.value;
                  // Si cambiamos de clase y la categoría actual no es compatible, la reseteamos
                  let newCategoria = filters.categoria;
                  if (newClase && CATEGORIAS_POR_CLASE[newClase] && !CATEGORIAS_POR_CLASE[newClase].includes(filters.categoria)) {
                    newCategoria = '';
                  }
                  setFilters({ ...filters, clase: newClase, categoria: newCategoria });
                }}
              >
                <option value="">Todo Clase</option>
                <option value="Activo">Activos</option>
                <option value="Periférico">Periféricos</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontSize: '10px', fontWeight: '800', color: 'var(--p-slate)', textTransform: 'uppercase' }}>Categoría:</label>
              <select 
                className="select-premium" 
                style={{ width: '160px', height: '38px' }} 
                value={filters.categoria} 
                onChange={e => setFilters({ ...filters, categoria: e.target.value })}
              >
                <option value="">Categorías</option>
                {(filters.clase ? CATEGORIAS_POR_CLASE[filters.clase] : allCategories).map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <button className="btn-secondary" style={{ height: '38px', padding: '0 15px', marginLeft: 'auto' }} onClick={() => {
              setFilters({ categoria: '', estado: '', periodo: 'all', startDate: '', endDate: '', historialUsuario: '', clase: '' });
              setTimeout(() => fetchDashboardData(), 0);
            }}>
              <i className="fa-solid fa-eraser"></i>
            </button>
            <button className="btn-primary" style={{ height: '38px', padding: '0 15px' }} onClick={handleExportCSV}>
              <i className="fa-solid fa-file-csv"></i> Reporte
            </button>
          </div>
        </section>

        {activeView === 'inventory' && (
          <div className="inventory-analytics-premium animate-fade-in">
            <div className="dashboard-widgets widgets-5col" style={{ marginBottom: '30px' }}>
              <div className="widget-card">
                <h3><i className="fa-solid fa-boxes-stacked" style={{ marginRight: '6px', opacity: 0.6 }}></i>Total Inventario</h3>
                <p className="big-number">{data.total}</p>
              </div>
              <div className="widget-card">
                <h3><i className="fa-solid fa-circle-check" style={{ marginRight: '6px', opacity: 0.6 }}></i>Saludables</h3>
                <p className="big-number">{data.active}</p>
                <span className="widget-trend pos">{(data.active / data.total * 100).toFixed(0)}% del total</span>
              </div>
              <div className="widget-card">
                <h3><i className="fa-solid fa-wrench" style={{ marginRight: '6px', opacity: 0.6 }}></i>Mantenimiento</h3>
                <p className="big-number">{data.maintenance}</p>
                <span className="widget-trend neg">Alerta Técnica</span>
              </div>
              <div className="widget-card">
                <h3><i className="fa-solid fa-user-check" style={{ marginRight: '6px', opacity: 0.6 }}></i>Asignados</h3>
                <p className="big-number">{data.assigned}</p>
                <span className="widget-trend pos">En Funcionarios</span>
              </div>
              <div className="widget-card">
                <h3><i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '6px', opacity: 0.6 }}></i>Bajas</h3>
                <p className="big-number">{data.retired}</p>
                <span className="widget-trend neg">Patrimonio</span>
              </div>
            </div>

            {!data ? (
              <div className="p-glass-card" style={{ padding: '60px', textAlign: 'center' }}>
                <i className="fa-solid fa-plug-circle-xmark" style={{ fontSize: '32px', color: 'var(--p-rose)', display: 'block', marginBottom: '15px', opacity: 0.6 }}></i>
                <h3 style={{ color: 'var(--p-navy)' }}>Error de Sincronización</h3>
                <p style={{ color: 'var(--p-slate)', fontSize: '13px' }}>No se pudo conectar con el servidor de inteligencia.</p>
              </div>
            ) : data.total === 0 ? (
              <div className="p-glass-card" style={{ padding: '60px', textAlign: 'center' }}>
                <i className="fa-solid fa-box-open" style={{ fontSize: '32px', color: 'var(--p-slate)', display: 'block', marginBottom: '15px', opacity: 0.4 }}></i>
                <h3 style={{ color: 'var(--p-navy)' }}>Inventario Vacío</h3>
                <p style={{ color: 'var(--p-slate)', fontSize: '13px' }}>No hay equipos registrados para analizar.</p>
              </div>
            ) : (
              <>
                <div className="analytics-layout-grid-premium">
                  <div className="chart-panel-premium">
                    <div className="panel-header">
                      <i className="fa-solid fa-chart-pie" style={{ color: 'var(--p-blue)', fontSize: '14px' }}></i>
                      <h3>Distribución por Estado</h3>
                    </div>
                    <div className="chart-container" style={{ height: '300px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={statusPieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            dataKey="value"
                            paddingAngle={5}
                            labelLine={{ stroke: 'var(--p-slate)', strokeWidth: 1 }}
                            label={renderExternalLabel}
                          >
                            {statusPieData.map((entry, index) => {
                              const statusLower = entry.name.toLowerCase();
                              const color = STATUS_COLORS[statusLower] || COLORS[index % COLORS.length];
                              return <Cell key={`cell-${index}`} fill={color} />;
                            })}
                          </Pie>
                          <ReTooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '12px', fontWeight: '600' }} />
                          <ReLegend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '11px', fontWeight: '800', color: '#64748b', paddingTop: '20px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="chart-panel-premium">
                    <div className="panel-header">
                      <i className="fa-solid fa-bell" style={{ color: 'var(--p-amber)', fontSize: '14px' }}></i>
                      <h3>Alertas y Utilización</h3>
                    </div>
                    <div className="p-alerts-feed">
                      {alerts.length > 0 ? alerts.map((a, i) => (
                        <div key={i} className={`p-alert-card ${a.type}`}>
                          <div className="alert-content">
                            <span className="alert-category">{a.type === 'danger' ? 'CRÍTICO' : a.type === 'warning' ? 'ADVERTENCIA' : 'INFO'}</span>
                            <p className="alert-text">{a.text}</p>
                          </div>
                        </div>
                      )) : (
                        <div className="p-empty-state">No hay alertas críticas en este periodo de análisis.</div>
                      )}

                      <div className="usage-meter-premium">
                        <div className="usage-header">
                          <span className="usage-label">Utilización del Inventario</span>
                          <span className="usage-value">{Math.round(data?.personnel?.utilization_pct || 0)}%</span>
                        </div>
                        <div className="premium-meter-rail">
                          <div
                            className="premium-meter-track"
                            style={{ width: `${data?.personnel?.utilization_pct || 0}%` }}
                          >
                            <div className="meter-glow"></div>
                          </div>
                        </div>
                        <p className="usage-hint">Ratio de equipos asignados vs total operativo</p>
                      </div>
                    </div>
                  </div>

                  <div className="chart-panel-premium panel-full-width">
                    <div className="panel-header">
                      <i className="fa-solid fa-chart-line" style={{ color: 'var(--p-indigo)', fontSize: '14px' }}></i>
                      <h3>Actividad y Tendencias</h3>
                    </div>

                    <div className="charts-insight-container charts-two-col" style={{ marginBottom: '24px' }}>
                      <div className="chart-box">
                        <p style={{ fontSize: '12px', fontWeight: '700', color: 'var(--p-slate)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}><i className="fa-solid fa-bars-staggered" style={{ fontSize: '10px', color: 'var(--p-indigo)' }}></i> Distribución de Equipos</p>
                        <div style={{ height: '240px' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={categoryBarData} layout="vertical" margin={{ right: 30 }}>
                              <XAxis type="number" hide />
                              <YAxis dataKey="name" type="category" width={80} axisLine={false} tickLine={false} style={{ fontSize: '12px', fontWeight: '700', fill: 'var(--p-navy)' }} />
                              <ReTooltip cursor={{ fill: 'rgba(99, 102, 241, 0.04)' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '11px' }} />
                              <Bar dataKey="value" fill="url(#colorIndigo)" radius={[0, 4, 4, 0]}>
                                <labelList dataKey="value" position="right" style={{ fill: 'var(--p-slate)', fontSize: '12px', fontWeight: '800' }} />
                              </Bar>
                              <defs>
                                <linearGradient id="colorIndigo" x1="0" y1="0" x2="1" y2="0">
                                  <stop offset="0%" stopColor="#6366f1" />
                                  <stop offset="100%" stopColor="#818cf8" />
                                </linearGradient>
                              </defs>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      <div className="chart-box">
                        <p style={{ fontSize: '12px', fontWeight: '700', color: 'var(--p-slate)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}><i className="fa-solid fa-wave-square" style={{ fontSize: '10px', color: 'var(--p-indigo)' }}></i> Ritmo de Movimientos</p>
                        <div style={{ height: '240px' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={trendData}>
                              <defs>
                                <linearGradient id="colorTrend" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <XAxis dataKey="dia" axisLine={false} tickLine={false} style={{ fontSize: '10px', fill: 'var(--p-slate)' }} tickFormatter={(val) => val.split('-').slice(1).join('/')} />
                              <YAxis axisLine={false} tickLine={false} style={{ fontSize: '10px', fill: 'var(--p-slate)' }} />
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--glass-border)" />
                              <ReTooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '11px' }} />
                              <Area type="monotone" dataKey="asignaciones" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorTrend)" name="Movimientos" />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>

                    <div className="behavior-insight-panel">
                      <div style={{ width: '44px', height: '44px', borderRadius: '14px', background: 'linear-gradient(135deg, #6366f1, #0ea5e9)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: 'white', flexShrink: 0, boxShadow: '0 6px 20px rgba(99, 102, 241, 0.3)' }}><i className="fa-solid fa-lightbulb"></i></div>
                      <div>
                        <h4 style={{ margin: '0 0 6px 0', fontSize: '14px', fontWeight: '800', color: 'var(--p-navy)' }}>Resumen de Tendencias</h4>
                        <p style={{ margin: 0, fontSize: '13px', color: 'var(--p-slate)', lineHeight: '1.6' }}>
                          {trendData.length > 0 ? (
                            <>Se detectó un pico de actividad el día <strong style={{ color: 'var(--p-navy)' }}>{trendData[trendData.length - 2]?.dia || 'reciente'}</strong>. La categoría predominante sigue siendo <strong style={{ color: 'var(--p-navy)' }}>{categoryBarData[0]?.name || 'N/A'}</strong> con {categoryBarData[0]?.value || 0} unidades.</>
                          ) : 'No hay datos suficientes para generar un análisis de tendencia en este rango.'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="chart-panel-premium panel-full-width">
                    <div className="panel-header" style={{ justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <i className="fa-solid fa-clock-rotate-left" style={{ color: 'var(--p-blue)', fontSize: '14px' }}></i>
                        <h3>Trazabilidad de Movimientos</h3>
                      </div>
                      <div className="history-filter-inline">
                        <select
                          className="filter-select"
                          value={filters.historialUsuario}
                          onChange={e => setFilters({ ...filters, historialUsuario: e.target.value })}
                          style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--glass-border)', fontSize: '12px', background: 'var(--input-bg)', fontWeight: '500', color: 'var(--p-slate)', outline: 'none' }}
                        >
                          <option value="">Filtrar por Admin / Operador</option>
                          {data?.uniqueUsers?.map((u, i) => (
                            <option key={i} value={u.usuario_uid}>{u.usuario_uid}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="activity-feed-compact" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      {history.length > 0 ? history.map((h, i) => (
                        <div className="activity-item-premium" key={i}>
                          <div className="activity-main">
                            <div className="activity-indicator"></div>
                            <div className="activity-content-box">
                              <div className="activity-meta-top">
                                <span className="p-badge-outline">{h.codigo}</span>
                                <span className="activity-time">{h.fecha}</span>
                              </div>
                              <p className="activity-text">{h.detalle}</p>
                            </div>
                          </div>
                          <div className="activity-user-badge">
                            <div className="u-avatar-mini">{h.usuario_uid?.charAt(0)}</div>
                            <span className="u-name-mini">{h.usuario_uid}</span>
                          </div>
                        </div>
                      )) : (
                        <div className="p-empty-state">No hay movimientos que coincidan con los filtros.</div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeView === 'personnel' && (
          <div className="personnel-analytics-premium animate-fade-in">
            <div className="panel-executive-header" style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'linear-gradient(135deg, var(--p-indigo), var(--p-blue))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '16px' }}>
                  <i className="fa-solid fa-users-gear"></i>
                </div>
                <div>
                  <h2 style={{ fontSize: '18px', fontWeight: '800', color: 'var(--p-navy)', margin: 0 }}>Análisis de Personal</h2>
                  <p style={{ margin: 0, fontSize: '11px', color: 'var(--p-slate)' }}>Distribución y carga por funcionario</p>
                </div>
              </div>
              <select
                className="select-premium"
                style={{ width: '220px', height: '42px' }}
                value={filters.departamento}
                onChange={e => setFilters({ ...filters, departamento: e.target.value })}
              >
                <option value="Todas">Todos los Departamentos</option>
                {funcData?.distributions?.equipsByDept?.map(d => (
                  <option key={d.name} value={d.name}>{d.name}</option>
                ))}
              </select>
            </div>

            {!funcData ? (
              <div className="p-glass-card" style={{ padding: '60px', textAlign: 'center' }}>
                <i className="fa-solid fa-plug-circle-xmark" style={{ fontSize: '32px', color: 'var(--p-rose)', display: 'block', marginBottom: '15px', opacity: 0.6 }}></i>
                <h3 style={{ color: 'var(--p-navy)' }}>Error de Sincronización</h3>
                <p style={{ color: 'var(--p-slate)', fontSize: '13px' }}>No se pudo conectar con el servidor de personal.</p>
              </div>
            ) : funcData.metrics?.totalFuncionarios === 0 ? (
              <div className="p-glass-card" style={{ padding: '60px', textAlign: 'center' }}>
                <i className="fa-solid fa-users-slash" style={{ fontSize: '32px', color: 'var(--p-slate)', display: 'block', marginBottom: '15px', opacity: 0.4 }}></i>
                <h3 style={{ color: 'var(--p-navy)' }}>Sin Datos de Personal</h3>
                <p style={{ color: 'var(--p-slate)', fontSize: '13px' }}>No se encontraron funcionarios registrados.</p>
              </div>
            ) : (
              <>
            <div className="dashboard-widgets widgets-4col" style={{ marginBottom: '30px' }}>
              <div className="widget-card">
                <h3><i className="fa-solid fa-users" style={{ marginRight: '6px', opacity: 0.6 }}></i>Personal Total</h3>
                <p className="big-number">{funcData?.metrics?.totalFuncionarios || 0}</p>
                <span className="widget-trend pos">Registrados</span>
              </div>
              <div className="widget-card">
                <h3><i className="fa-solid fa-user-check" style={{ marginRight: '6px', opacity: 0.6 }}></i>Responsables</h3>
                <p className="big-number">{funcData?.metrics?.conEquipos || 0}</p>
                <span className="widget-trend pos">Con Activos</span>
              </div>
              <div className="widget-card">
                <h3><i className="fa-solid fa-user-xmark" style={{ marginRight: '6px', opacity: 0.6 }}></i>Sin Asignación</h3>
                <p className="big-number">{funcData?.metrics?.sinEquipos || 0}</p>
                <span className="widget-trend neg">Sin Inventario</span>
              </div>
              <div className="widget-card">
                <h3><i className="fa-solid fa-scale-balanced" style={{ marginRight: '6px', opacity: 0.6 }}></i>Carga Promedio</h3>
                <p className="big-number">
                  {funcData?.metrics?.totalFuncionarios > 0
                    ? (funcData?.metrics?.totalEquips / funcData?.metrics?.totalFuncionarios).toFixed(1)
                    : '0.0'}
                </p>
                <span className="widget-trend pos">Equipos/Func.</span>
              </div>
            </div>

                <div className="analytics-layout-grid-premium">
                  <div className="chart-panel-premium">
                    <div className="panel-header">
                      <i className="fa-solid fa-chart-pie" style={{ color: 'var(--p-blue)', fontSize: '14px' }}></i>
                      <h3>Equipos por Departamento</h3>
                    </div>
                    <div className="chart-container" style={{ height: '320px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={funcData?.distributions?.equipsByDept || []}
                            cx="50%" cy="50%"
                            innerRadius={70}
                            outerRadius={100}
                            paddingAngle={8}
                            dataKey="value"
                            labelLine={{ stroke: 'var(--p-slate)', strokeWidth: 1 }}
                            label={renderExternalLabel}
                          >
                            {(funcData?.distributions?.equipsByDept || []).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="rgba(255,255,255,0.2)" strokeWidth={2} />
                            ))}
                          </Pie>
                          <ReTooltip contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '11px', fontWeight: '800' }} />
                          <ReLegend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '11px', fontWeight: '800', color: '#64748b' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="chart-panel-premium">
                    <div className="panel-header">
                      <i className="fa-solid fa-bell" style={{ color: 'var(--p-rose)', fontSize: '14px' }}></i>
                      <h3>Alertas de Distribución</h3>
                    </div>
                    <div className="p-alerts-feed">
                      {funcData?.alerts?.length > 0 ? funcData.alerts.map((a, i) => (
                        <div key={i} className={`p-alert-card ${a.type}`}>
                          <div className="alert-content">
                            <span className="alert-category">{a.type === 'danger' ? 'CRÍTICO' : a.type === 'warning' ? 'ADVERTENCIA' : 'INFO'}</span>
                            <p className="alert-text">{a.text}</p>
                          </div>
                        </div>
                      )) : (
                        <div className="p-empty-state">Distribución de activos equilibrada y sin novedades.</div>
                      )}
                    </div>
                  </div>

                  <div className="chart-panel-premium panel-full-width">
                    <div className="panel-header">
                      <i className="fa-solid fa-ranking-star" style={{ color: 'var(--p-indigo)', fontSize: '14px' }}></i>
                      <h3>Carga por Funcionario</h3>
                    </div>
                    <div className="p-ranking-table">
                      <table className="func-table-premium">
                        <thead>
                          <tr>
                            <th><i className="fa-solid fa-user" style={{ marginRight: '6px', opacity: 0.6 }}></i>Funcionario</th>
                            <th><i className="fa-solid fa-building" style={{ marginRight: '6px', opacity: 0.6 }}></i>Departamento</th>
                            <th><i className="fa-solid fa-laptop" style={{ marginRight: '6px', opacity: 0.6 }}></i>Activos</th>
                            <th style={{ width: '150px' }}>Nivel de Carga</th>
                          </tr>
                        </thead>
                        <tbody>
                          {funcData?.individuals?.slice(0, 8).map(f => (
                            <tr key={f.id} className="row-hover">
                              <td>
                                <div className="f-identity">
                                  <div className="f-avatar" style={{ background: COLORS[f.id?.length % COLORS.length] }}>{f.nombre?.charAt(0)}</div>
                                  <div className="f-meta">
                                    <span className="f-name" style={{ color: 'var(--p-navy)', fontWeight: 'bold' }}>{f.nombre}</span>
                                    <span className="f-tag" style={{ color: 'var(--p-slate)', fontSize: '12px' }}>{f.cargo}</span>
                                  </div>
                                </div>
                              </td>
                              <td><span className="p-badge-outline" style={{ opacity: 0.7, color: 'var(--p-slate)', borderColor: 'var(--glass-border)' }}>{f.departamento || 'General'}</span></td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span className="f-count" style={{ fontSize: '16px' }}>{f.total_equipos}</span>
                                  <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: '700' }}>equipos</span>
                                </div>
                              </td>
                              <td>
                                <div className="meter-rail" style={{ height: '8px', background: 'var(--sidebar-accent)', borderRadius: '10px', overflow: 'hidden', width: '100px', border: '1px solid var(--glass-border)' }}>
                                  <div className="meter-track" style={{
                                    width: `${Math.min((f.total_equipos / 6) * 100, 100)}%`,
                                    height: '100%',
                                    background: f.total_equipos > 5 ? 'linear-gradient(90deg, #f43f5e, #fb7185)' : f.total_equipos > 3 ? 'linear-gradient(90deg, #f59e0b, #fbbf24)' : 'linear-gradient(90deg, #6366f1, #818cf8)',
                                    borderRadius: '10px',
                                    transition: 'width 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
                                    boxShadow: f.total_equipos > 3 ? '0 0 12px rgba(245, 158, 11, 0.15)' : '0 0 12px rgba(99, 102, 241, 0.15)'
                                  }}></div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

      </main>
    </div>
  );
};

export default SupervisorDashboard;
